import { Fragment, ReactNode, createElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { toast } from 'react-toastify'

import { getCodeBlockPresentation } from '../../utils/markdown'

type CodeBlockProps = {
  code: string
  language?: string
}

const COPY_RESET_DELAY = 1500

const CopyIcon = () => (
  <svg
    className="md-code-copy-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
)

const renderHighlightedNode = (node: Node, key: string): ReactNode => {
  if (node.nodeType === Node.TEXT_NODE || node.nodeType === Node.CDATA_SECTION_NODE) {
    return node.textContent ?? ''
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return ''
  }

  const element = node as Element
  const children = Array.from(element.childNodes).map((child, index) =>
    renderHighlightedNode(child, `${key}-${index}`)
  )

  if (element.tagName.toLowerCase() !== 'span') {
    return createElement(Fragment, { key }, ...children)
  }

  const className = element.getAttribute('class')
  return createElement('span', className ? { key, className } : { key }, ...children)
}

/**
 * highlight.js returns markup; it is re-parsed and rebuilt as elements so no HTML string
 * is ever injected into the transcript.
 */
const parseHighlightedCode = (html: string, fallback: string): ReactNode[] => {
  if (typeof DOMParser === 'undefined') {
    return [fallback]
  }

  const parsed = new DOMParser().parseFromString(`<code>${html}</code>`, 'text/html')
  const root = parsed.body.firstElementChild
  if (!root) {
    return [fallback]
  }

  return Array.from(root.childNodes).map((child, index) =>
    renderHighlightedNode(child, `hl-${index}`)
  )
}

const CodeBlock = ({ code, language = '' }: CodeBlockProps) => {
  const [copied, setCopied] = useState(false)
  const copyResetTimeoutRef = useRef<number | null>(null)

  const presentation = useMemo(() => getCodeBlockPresentation(code, language), [code, language])
  const highlightedChildren = useMemo(
    () => parseHighlightedCode(presentation.highlightedCode, code),
    [code, presentation.highlightedCode]
  )

  useEffect(() => {
    return () => {
      if (copyResetTimeoutRef.current !== null) {
        window.clearTimeout(copyResetTimeoutRef.current)
        copyResetTimeoutRef.current = null
      }
    }
  }, [])

  const copyCode = useCallback(async (): Promise<void> => {
    if (!code) {
      toast.error('Nothing to copy')
      return
    }

    try {
      await navigator.clipboard.writeText(code)
      setCopied(true)

      if (copyResetTimeoutRef.current !== null) {
        window.clearTimeout(copyResetTimeoutRef.current)
      }
      copyResetTimeoutRef.current = window.setTimeout(() => {
        copyResetTimeoutRef.current = null
        setCopied(false)
      }, COPY_RESET_DELAY)

      toast.success('Copied!')
    } catch {
      toast.error('Something went wrong')
    }
  }, [code])

  return (
    <div className="md-code-block">
      <div className="md-code-header">
        <span className="md-code-language">{presentation.languageLabel}</span>
        <button
          type="button"
          className={`md-code-copy-btn${copied ? ' is-copied' : ''}`}
          aria-label={copied ? 'Copied' : 'Copy code'}
          title={copied ? 'Copied' : 'Copy code'}
          onClick={copyCode}
        >
          <CopyIcon />
        </button>
      </div>
      <pre className="md-code-pre">
        <code className={`hljs language-${presentation.languageClass}`}>{highlightedChildren}</code>
      </pre>
    </div>
  )
}

export default CodeBlock
